/**
 * Resolving a `DmxTarget` to the fixtures it actually names, and to the words
 * the operator reads.
 *
 * Two jobs, deliberately in one module because they must not disagree: a
 * target that RESOLVES to three fixtures and READS as "Front Wash" when the
 * group holds four is worse than either being wrong alone.
 *
 * ## Resolution is CLIENT-SIDE, and that is a decision
 *
 * OPBridge's drive routes are per-fixture-id (`POST /dmx/fixtures/{id}/...`).
 * There is no group route and no all route, so `all` and `group` fan out to N
 * requests here. Considered asking OPBridge for a group endpoint and did not,
 * for three reasons:
 *
 *  - **N is the size of a rig.** Five on this operator's. A venue rig of
 *    thirty is still thirty requests against a 50 r/s bucket, once per tap.
 *  - **A group is OPButtons-facing.** OPBridge stores the tags because they
 *    live on the fixture record, but "which lights does the operator think of
 *    as one thing" is a control-surface question. Pushing it down would make
 *    two products own one concept.
 *  - **Partial success stays legible.** A fan-out reports per-fixture, so
 *    "four of five went" is sayable. A single group call that half-failed
 *    would have to invent that reporting anyway.
 *
 * ## An empty resolution is NOT the same as a failure
 *
 * A group whose every fixture has since been deleted resolves to zero
 * fixtures. That is a configuration problem with a specific fix (retag, or
 * repoint the control), and it must never read as "sent" nor as "the bridge
 * is down". `resolveTarget` returns the list; callers check `.length === 0`
 * and say the specific thing. Same discipline as `bridgeSync`'s multi-valued
 * status: collapsing three problems into one empty value is how the retired
 * Stream Deck stub misled everyone for a year.
 */

import type { DmxTarget, FixtureInstance } from '@opbuttons/schema';
import { fixtures, fixtureZones } from '../fixtures/store.ts';

/** Every group tag in use across the rig, de-duplicated and sorted. The
 *  source of the group picker's options - there is no separate group registry,
 *  because a group with no fixtures in it is not a group, it is a typo. */
export function allGroups(): string[] {
  const seen = new Set<string>();
  for (const f of fixtures()) for (const g of f.groups ?? []) seen.add(g);
  return [...seen].sort((a, b) => a.localeCompare(b));
}

/**
 * The fixtures a target names, in rig order.
 *
 * `zone` resolves to the same single fixture as `fixture` - the zone index
 * rides alongside on the wire rather than selecting a different object. The
 * caller passes it through; see `targetSubfixture`.
 */
export function resolveTarget(target: DmxTarget): FixtureInstance[] {
  const rig = fixtures();
  switch (target.kind) {
    case 'all':
      return rig;
    case 'group':
      return rig.filter((f) => (f.groups ?? []).includes(target.group));
    case 'fixture':
    case 'zone': {
      const f = rig.find((x) => x.id === target.fixtureId);
      return f ? [f] : [];
    }
    default:
      return [];
  }
}

/** The zone index to send, or undefined for a whole-fixture write. Only a
 *  `zone` target has one; every other kind addresses whole fixtures, and for
 *  a zoned fixture that FANS to every zone (OPBridge's documented behaviour,
 *  not ours). */
export function targetSubfixture(target: DmxTarget): number | undefined {
  return target.kind === 'zone' ? target.subfixture : undefined;
}

/**
 * What the operator reads on the control's face and in the Inspector.
 *
 * Never an id. A fixture whose id resolves to nothing reads "light removed",
 * matching the loud-dead-state convention (DEC-124/138) the Rig State and
 * Helix tiles already use - a control pointing at a deleted thing must say
 * so, not fall back to something plausible.
 */
export function describeTarget(target: DmxTarget): string {
  const rig = fixtures();
  switch (target.kind) {
    case 'all': {
      const n = rig.length;
      return n === 0 ? 'no lights in rig' : 'All lights';
    }
    case 'group': {
      const n = rig.filter((f) => (f.groups ?? []).includes(target.group)).length;
      if (n === 0) return `"${target.group}" - group is empty`;
      return `${target.group} (${n})`;
    }
    case 'fixture': {
      const f = rig.find((x) => x.id === target.fixtureId);
      return f ? f.name : 'light removed';
    }
    case 'zone': {
      const f = rig.find((x) => x.id === target.fixtureId);
      if (!f) return 'light removed';
      const zone = fixtureZones(f).find((z) => z.index === target.subfixture);
      // ZONE FIRST. A face label truncates at the end, so whatever leads has
      // to be the discriminator - and for "which of these four blocks" that is
      // the block, not the make and model. It matters most exactly where the
      // label is longest: an unnamed fixture synthesises something like
      // "Colorband Pix · 1.71", and fixture-first pushed the block clean off
      // the end of the button.
      //
      // The `zone gone` branch is its own failure and keeps the FIXTURE first,
      // because there is no zone left to lead with: the operator may have
      // re-patched into a mode with fewer blocks, and saying so beats silently
      // addressing the whole fixture, which would light three zones they did
      // not ask for.
      return zone ? `${zone.name} · ${f.name}` : `${f.name} - zone gone`;
    }
    default:
      return 'no target';
  }
}

/** True when the target names at least one fixture that exists right now.
 *  What a control's live/dead state is computed from. */
export function targetIsLive(target: DmxTarget): boolean {
  if (target.kind === 'zone') {
    const f = resolveTarget(target)[0];
    return !!f && fixtureZones(f).some((z) => z.index === target.subfixture);
  }
  return resolveTarget(target).length > 0;
}

/** Why a target is dead, for the face's sub-label. Returns undefined when it
 *  is live. Distinguishes the three causes, because they have three different
 *  fixes: an empty rig, an empty group, and a deleted fixture. */
export function targetDeadReason(target: DmxTarget): string | undefined {
  if (targetIsLive(target)) return undefined;
  switch (target.kind) {
    case 'all':     return 'no lights in rig';
    case 'group':   return `group "${target.group}" is empty`;
    case 'fixture': return 'light removed';
    case 'zone':    return resolveTarget(target).length === 0 ? 'light removed' : 'zone gone';
    default:        return 'no target';
  }
}

/** Stable key for a target, for `<For>` keying and equality checks in
 *  pickers. Not persisted and not a wire value. */
export function targetKey(target: DmxTarget): string {
  switch (target.kind) {
    case 'all':     return 'all';
    case 'group':   return `group:${target.group}`;
    case 'fixture': return `fixture:${target.fixtureId}`;
    case 'zone':    return `zone:${target.fixtureId}:${target.subfixture}`;
    default:        return 'none';
  }
}
